import type { EmploymentType } from './resourceplanning/index.js';
import { RESOURCE_TYPE_OTHER, RESOURCE_TYPE_PERSON } from './resourceplanning/index.js';

/**
 * The canonical resource-organisation fixture (PM-2019, M1): two legal
 * entities, a three-level department tree, eight roles and 25 resources with
 * stable ids — so seeds, tests and later capacity/allocation fixtures agree.
 * Plain data only; the seed script writes it through the org repository.
 */

export type ResourceOrgLegalEntityFixture = {
  id: string;
  externalId: string | null;
  name: string;
};

export type ResourceOrgDepartmentFixture = {
  id: string;
  externalId: string | null;
  number: string | null;
  name: string;
  parentId: string | null;
};

export type ResourceOrgRoleFixture = {
  id: string;
  name: string;
};

export type ResourceOrgResourceRoleFixture = {
  roleId: string;
  isPrimary: boolean;
};

export type ResourceOrgResourceFixture = {
  id: string;
  externalId: string | null;
  name: string;
  resourceType: string;
  employmentType: EmploymentType | null;
  legalEntityId: string;
  departmentId: string;
  roles: ResourceOrgResourceRoleFixture[];
};

export type ResourceOrgFixture = {
  legalEntities: ResourceOrgLegalEntityFixture[];
  departments: ResourceOrgDepartmentFixture[];
  roles: ResourceOrgRoleFixture[];
  resources: ResourceOrgResourceFixture[];
};

/** Stable ids (RFC 4122 v4 shape, like SHIP_NODE_IDS). */
export const ORG_LEGAL_ENTITY_IDS = {
  nordicAs: '00000000-0000-4000-8000-00000000e001',
  nordicGmbh: '00000000-0000-4000-8000-00000000e002',
} as const;

export const ORG_DEPARTMENT_IDS = {
  engineering: '00000000-0000-4000-8000-00000000d101',
  backend: '00000000-0000-4000-8000-00000000d102',
  platform: '00000000-0000-4000-8000-00000000d103',
  frontend: '00000000-0000-4000-8000-00000000d104',
  consulting: '00000000-0000-4000-8000-00000000d105',
  advisory: '00000000-0000-4000-8000-00000000d106',
  delivery: '00000000-0000-4000-8000-00000000d107',
  operations: '00000000-0000-4000-8000-00000000d108',
} as const;

export const ORG_ROLE_IDS = {
  seniorBackendEngineer: '00000000-0000-4000-8000-00000000c201',
  frontendEngineer: '00000000-0000-4000-8000-00000000c202',
  principalDeveloper: '00000000-0000-4000-8000-00000000c203',
  projectManager: '00000000-0000-4000-8000-00000000c204',
  uxDesigner: '00000000-0000-4000-8000-00000000c205',
  qaEngineer: '00000000-0000-4000-8000-00000000c206',
  dataEngineer: '00000000-0000-4000-8000-00000000c207',
  businessConsultant: '00000000-0000-4000-8000-00000000c208',
} as const;

/** Resource n (1-based) → its stable fixture id. */
export const orgResourceId = (n: number): string =>
  `00000000-0000-4000-8000-00000000f2${String(n).padStart(2, '0')}`;

const E = ORG_LEGAL_ENTITY_IDS;
const D = ORG_DEPARTMENT_IDS;
const R = ORG_ROLE_IDS;

type PersonSpec = {
  name: string;
  employmentType: EmploymentType;
  legalEntityId: string;
  departmentId: string;
  primaryRoleId: string;
  secondaryRoleId?: string;
};

const PEOPLE: PersonSpec[] = [
  // Engineering / Backend (Nordic A/S)
  {
    name: 'Mette Sørensen',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicAs,
    departmentId: D.backend,
    primaryRoleId: R.seniorBackendEngineer,
    secondaryRoleId: R.dataEngineer,
  },
  {
    name: 'Jonas Lindqvist',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicAs,
    departmentId: D.backend,
    primaryRoleId: R.seniorBackendEngineer,
  },
  {
    name: 'Freja Holm',
    employmentType: 'FIXED_TERM',
    legalEntityId: E.nordicAs,
    departmentId: D.backend,
    primaryRoleId: R.seniorBackendEngineer,
    secondaryRoleId: R.qaEngineer,
  },
  {
    name: 'Oliver Dam',
    employmentType: 'CONTRACTOR',
    legalEntityId: E.nordicAs,
    departmentId: D.backend,
    primaryRoleId: R.dataEngineer,
  },
  // Engineering / Backend / Platform
  {
    name: 'Astrid Vinter',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicAs,
    departmentId: D.platform,
    primaryRoleId: R.principalDeveloper,
    secondaryRoleId: R.seniorBackendEngineer,
  },
  {
    name: 'Emil Krog',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicAs,
    departmentId: D.platform,
    primaryRoleId: R.seniorBackendEngineer,
  },
  {
    name: 'Sofia Bergström',
    employmentType: 'CONTRACTOR',
    legalEntityId: E.nordicAs,
    departmentId: D.platform,
    primaryRoleId: R.dataEngineer,
    secondaryRoleId: R.seniorBackendEngineer,
  },
  // Engineering / Frontend
  {
    name: 'Lucas Nørgaard',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicAs,
    departmentId: D.frontend,
    primaryRoleId: R.frontendEngineer,
  },
  {
    name: 'Ida Månsson',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicAs,
    departmentId: D.frontend,
    primaryRoleId: R.frontendEngineer,
    secondaryRoleId: R.uxDesigner,
  },
  {
    name: 'Noah Vestergaard',
    employmentType: 'FIXED_TERM',
    legalEntityId: E.nordicAs,
    departmentId: D.frontend,
    primaryRoleId: R.frontendEngineer,
  },
  {
    name: 'Clara Ejlersen',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicAs,
    departmentId: D.frontend,
    primaryRoleId: R.uxDesigner,
  },
  {
    name: 'William Toft',
    employmentType: 'CONTRACTOR',
    legalEntityId: E.nordicAs,
    departmentId: D.frontend,
    primaryRoleId: R.qaEngineer,
    secondaryRoleId: R.frontendEngineer,
  },
  // Consulting / Advisory (Nordic A/S)
  {
    name: 'Alma Riis',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicAs,
    departmentId: D.advisory,
    primaryRoleId: R.businessConsultant,
  },
  {
    name: 'Oscar Lindegaard',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicAs,
    departmentId: D.advisory,
    primaryRoleId: R.businessConsultant,
    secondaryRoleId: R.projectManager,
  },
  {
    name: 'Ella Brandt',
    employmentType: 'FIXED_TERM',
    legalEntityId: E.nordicAs,
    departmentId: D.advisory,
    primaryRoleId: R.projectManager,
  },
  // Consulting / Delivery
  {
    name: 'Viktor Aagaard',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicAs,
    departmentId: D.delivery,
    primaryRoleId: R.projectManager,
  },
  {
    name: 'Maja Enevoldsen',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicAs,
    departmentId: D.delivery,
    primaryRoleId: R.projectManager,
    secondaryRoleId: R.businessConsultant,
  },
  {
    name: 'Anton Skov',
    employmentType: 'CONTRACTOR',
    legalEntityId: E.nordicAs,
    departmentId: D.delivery,
    primaryRoleId: R.businessConsultant,
  },
  // Operations (Nordic GmbH)
  {
    name: 'Lena Fischer',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicGmbh,
    departmentId: D.operations,
    primaryRoleId: R.projectManager,
  },
  {
    name: 'Paul Wagner',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicGmbh,
    departmentId: D.operations,
    primaryRoleId: R.qaEngineer,
  },
  {
    name: 'Hannah Becker',
    employmentType: 'FIXED_TERM',
    legalEntityId: E.nordicGmbh,
    departmentId: D.operations,
    primaryRoleId: R.dataEngineer,
    secondaryRoleId: R.qaEngineer,
  },
  {
    name: 'Felix Hoffmann',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicGmbh,
    departmentId: D.backend,
    primaryRoleId: R.seniorBackendEngineer,
  },
  {
    name: 'Marie Schulz',
    employmentType: 'CONTRACTOR',
    legalEntityId: E.nordicGmbh,
    departmentId: D.frontend,
    primaryRoleId: R.frontendEngineer,
    secondaryRoleId: R.uxDesigner,
  },
  {
    name: 'Jan Krüger',
    employmentType: 'PERMANENT',
    legalEntityId: E.nordicGmbh,
    departmentId: D.advisory,
    primaryRoleId: R.businessConsultant,
  },
];

export const createResourceOrgFixture = (): ResourceOrgFixture => {
  const resources: ResourceOrgResourceFixture[] = PEOPLE.map((person, index) => ({
    id: orgResourceId(index + 1),
    externalId: null,
    name: person.name,
    resourceType: RESOURCE_TYPE_PERSON,
    employmentType: person.employmentType,
    legalEntityId: person.legalEntityId,
    departmentId: person.departmentId,
    roles: [
      { roleId: person.primaryRoleId, isPrimary: true },
      ...(person.secondaryRoleId === undefined
        ? []
        : [{ roleId: person.secondaryRoleId, isPrimary: false }]),
    ],
  }));

  // The 25th resource is a non-person: no employmentType, no roles (PM-2017).
  resources.push({
    id: orgResourceId(25),
    externalId: null,
    name: 'License — Enterprise CI pool',
    resourceType: RESOURCE_TYPE_OTHER,
    employmentType: null,
    legalEntityId: E.nordicAs,
    departmentId: D.operations,
    roles: [],
  });

  return {
    legalEntities: [
      { id: E.nordicAs, externalId: null, name: 'Nordic Consulting A/S' },
      { id: E.nordicGmbh, externalId: null, name: 'Nordic Consulting GmbH' },
    ],
    departments: [
      { id: D.engineering, externalId: null, number: '100', name: 'Engineering', parentId: null },
      { id: D.backend, externalId: null, number: '110', name: 'Backend', parentId: D.engineering },
      { id: D.platform, externalId: null, number: '111', name: 'Platform', parentId: D.backend },
      {
        id: D.frontend,
        externalId: null,
        number: '120',
        name: 'Frontend',
        parentId: D.engineering,
      },
      { id: D.consulting, externalId: null, number: '200', name: 'Consulting', parentId: null },
      { id: D.advisory, externalId: null, number: '210', name: 'Advisory', parentId: D.consulting },
      { id: D.delivery, externalId: null, number: '220', name: 'Delivery', parentId: D.consulting },
      { id: D.operations, externalId: null, number: '300', name: 'Operations', parentId: null },
    ],
    roles: [
      { id: R.seniorBackendEngineer, name: 'Senior Backend Engineer' },
      { id: R.frontendEngineer, name: 'Frontend Engineer' },
      { id: R.principalDeveloper, name: 'Principal Developer' },
      { id: R.projectManager, name: 'Project Manager' },
      { id: R.uxDesigner, name: 'UX Designer' },
      { id: R.qaEngineer, name: 'QA Engineer' },
      { id: R.dataEngineer, name: 'Data Engineer' },
      { id: R.businessConsultant, name: 'Business Consultant' },
    ],
    resources,
  };
};
